import { ParticipantStatus } from '@/features/dashboard/participants/types/ParticipantStatus.ts';
import { formatPhoneNumber } from '@/types/PhoneNumberDto.utils.ts';
import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import {
  CustomFieldValueDto,
  ParticipantCustomValuesDto,
} from '@/types/CustomFieldValueDto.ts';
import CustomFieldDefinitionDto from '@/features/dashboard/tourCustomFields/types/_CustomFieldDefinitionDto.ts';
import { DropdownCustomFieldDefinitionDto } from '@/features/dashboard/tourCustomFields/types/DropdownCustomFieldDefinitionDto.ts';
import {
  TourOptionDto,
  TourOptionVariantDto,
} from '@/features/dashboard/tourOptions/types/TourOptionDto.ts';
import {
  TransportConfigurationWebDto,
  TransportVariantDto,
} from '@/features/dashboard/tourTransport/types/TransportConfigurationDto.ts';
import { TransportOffering } from '@/features/dashboard/tourTransport/types/TransportOffering.ts';
import {
  isOfferedInsurance,
  TourInsurancesDto,
} from '@/features/dashboard/tourInsurances/types/TourInsurancesDto.ts';
import { ParticipantInsuranceDto } from '@/features/dashboard/participants/types/ParticipantInsuranceDto.ts';
import { ParticipantSelectedOptionsDto } from '@/features/dashboard/participants/types/SelectedOptionDto.ts';
import { SelectedTransportVariantDto } from '@/features/dashboard/participants/types/SelectedTransportVariantDto.ts';
import {
  BookingClientDto,
  BookingDetailsDto,
  BookingParticipantDto,
} from '@/features/dashboard/bookings/types/BookingDetailsDto.ts';
import {
  BookingBillDto,
  CostLineDto,
  CostLineType,
  costLinesOf,
} from '@/features/dashboard/bookings/types/BookingBillDto.ts';
import { ParticipantSelectionsDto } from '@/features/dashboard/bookings/types/ParticipantSelectionsDto.ts';
import {
  BillCatalog,
  BillLine,
  BillLineRef,
  BillSection,
  BillVariant,
  BillVariantPrice,
  BookingBill,
  ClientBill,
  ParticipantBill,
} from '@/features/dashboard/bookingChanges/types/BookingBill.ts';
import {
  BILL_SECTION_HINTS,
  BILL_SECTION_TITLES,
  BillSectionId,
} from '@/features/dashboard/bookingChanges/types/BillSectionId.ts';
import { BillPaymentDto } from '@/features/dashboard/bookingChanges/types/dto/BillPaymentDto.ts';
import { ChangeItemType } from '@/features/dashboard/bookingChanges/types/dto/ChangeItemDto.ts';
import { ClientBillField } from '@/features/dashboard/bookingChanges/types/dto/ClientBillField.ts';
import { ParticipantBillField } from '@/features/dashboard/bookingChanges/types/dto/ParticipantBillField.ts';
import { costRowKey } from '@/features/dashboard/bookingChanges/types/ChangePreview.ts';
import { NO_SELECTION_LABEL } from '@/features/dashboard/bookingChanges/utils/draftItems.ts';
import { formatDate } from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import { sumMoney } from '@/features/dashboard/bookingChanges/utils/money.ts';
import { EMPTY_VALUE } from '@/components/ui/EmptyValue.tsx';

/**
 * Złożenie rachunku rezerwacji z tego, co backend naprawdę wystawia.
 *
 * Pięć źródeł, każde o czym innym:
 * - **księga cen** (`GET /bookings/{id}/bill`) — kwoty i nazwy zamrożone
 *   w chwili zakupu, więc wiersz kwotowy rysuje się bez sięgania do katalogu;
 * - **rezerwacja** (`GET /bookings/{id}`) — dane osób, raty, saldo;
 * - **wybory uczestników** (`GET /bookings/{id}/participant-selections`) —
 *   czego nie widać w księdze, bo nie kosztuje; jednym żądaniem na całą
 *   rezerwację, niezależnie od liczby osób;
 * - **katalog wyjazdu** — pola niestandardowe, opcje, transport, ubezpieczenia;
 *   z niego biorą się wiersze **niewybrane**, bez których nie dałoby się
 *   niczego dodać;
 * - **wpłaty** — jedyne, co zostaje na mocku (patrz `bookingChangesApi.ts`).
 *
 * Sekcje i ich kolejność są decyzją frontu (`BillSectionId`) — backend definicji
 * sekcji nie wystawia i nie będzie. Sumowanie pieniędzy nie jest: `totals`
 * i `totalsByParticipant` przechodzą z księgi nietknięte.
 *
 * Waluty się tu nie odsiewa: rezerwacja bywa wielowalutowa, więc kwota wiersza
 * jest listą po jednej pozycji na walutę, a wiersz opcji wielokrotnego wyboru
 * potrafi zebrać dwie naraz.
 */

/** Domyślne pola wiersza — nazwane raz, żeby każdy wiersz nie powtarzał ośmiu `false`. */
const line = (
  id: string,
  label: string,
  type: FieldType,
  ref: BillLineRef | null,
  options: Partial<Omit<BillLine, 'id' | 'label' | 'type' | 'ref'>> = {},
): BillLine => ({
  id,
  label,
  type,
  ref,
  costLineIds: options.costLineIds ?? [],
  costKey: options.costKey ?? null,
  value: options.value ?? null,
  chips: options.chips ?? null,
  amount: options.amount ?? [],
  amountLabel: options.amountLabel ?? null,
  amountMuted: options.amountMuted ?? false,
  muted: options.muted ?? false,
  locked: options.locked ?? false,
  manual: options.manual ?? false,
  priceOverridden: options.priceOverridden ?? false,
  priceNote: options.priceNote ?? null,
  selectedAt: options.selectedAt,
});

const ref = (
  kind: ChangeItemType,
  options: Partial<Omit<BillLineRef, 'kind'>> = {},
): BillLineRef => ({
  kind,
  targetId: options.targetId ?? null,
  fields: options.fields ?? null,
  currentVariantId: options.currentVariantId ?? null,
  currentVariantIds: options.currentVariantIds ?? null,
});

const section = (
  personId: string,
  sectionId: BillSectionId,
  lines: BillLine[],
  options: Partial<
    Pick<BillSection, 'allowManualLines' | 'withAmounts' | 'note'>
  > = {},
): BillSection => ({
  id: `${personId}:${sectionId}`,
  title: BILL_SECTION_TITLES[sectionId],
  hint: BILL_SECTION_HINTS[sectionId] ?? null,
  note: options.note ?? null,
  allowManualLines: options.allowManualLines ?? false,
  withAmounts: options.withAmounts ?? false,
  lines,
});

/** Wiersz pustego wyboru: „Nie wybrano” i myślnik zamiast kwoty. */
const emptySelection = (withAmount: boolean): Partial<BillLine> => ({
  value: NO_SELECTION_LABEL,
  muted: true,
  ...(withAmount ? { amountLabel: EMPTY_VALUE, amountMuted: true } : {}),
});

const isTypeOf =
  <T extends CostLineType>(type: T) =>
  (candidate: CostLineDto): candidate is Extract<CostLineDto, { type: T }> =>
    candidate.type === type;

/*
 * Katalog: nazwy i ceny wartości, którymi wolno podmienić bieżący wybór.
 * Bierze się z konfiguracji wyjazdu, a nie z księgi — księga zna wyłącznie to,
 * co uczestnik już kupił. Grupowanie po opcji, po polu i po rodzaju wiersza
 * przechodzi z konfiguracji nietknięte: to ono decyduje, że modal edycji
 * warsztatów pokazuje warianty warsztatów, a nie cały cennik rezerwacji.
 *
 * Wartości wycofane z oferty **zostają** w katalogu z `archived: true` — bez
 * nich wiersz uczestnika, który wycofany wariant ma, nie umiałby go nazwać.
 * Odsiewa je dopiero lista wyboru (`resolveFieldOptions`).
 */

const optionVariantPrices = (
  variant: TourOptionVariantDto,
): BillVariantPrice[] =>
  variant.prices
    .filter((price) => !price.archived)
    .map((price) => ({
      price: price.price,
      minAge: price.ageRestriction?.minAge ?? null,
      maxAge: price.ageRestriction?.maxAge ?? null,
    }));

const transportVariantPrices = (
  variant: TransportVariantDto,
): BillVariantPrice[] =>
  variant.prices.map((price) => ({
    price: { amount: price.amount, currency: variant.currency },
    minAge: price.ageRange?.minAge ?? null,
    maxAge: price.ageRange?.maxAge ?? null,
  }));

const catalogVariant = (
  id: string,
  name: string,
  prices: BillVariantPrice[] = [],
  archived = false,
): BillVariant => ({ id, name, prices, archived });

/** Opcje pola listy rozwijanej; pozostałe rodzaje pól niestandardowych list nie mają. */
const customFieldOptionsOf = (
  definitions: CustomFieldDefinitionDto[],
): Record<string, BillVariant[]> =>
  Object.fromEntries(
    definitions
      .filter((definition) => definition.type === FieldType.DROPDOWN)
      .map((definition) => [
        definition.id,
        ((definition as DropdownCustomFieldDefinitionDto).options ?? [])
          .filter((option) => !!option.id)
          .map((option) => catalogVariant(option.id!, option.label)),
      ]),
  );

const buildCatalog = (
  customFields: CustomFieldDefinitionDto[],
  tourOptions: TourOptionDto[],
  transport: TransportConfigurationWebDto | null,
  tourInsurances: TourInsurancesDto,
): BillCatalog => ({
  optionVariants: Object.fromEntries(
    tourOptions.map((option) => [
      option.id,
      option.variants
        .filter((variant) => !!variant.id)
        .map((variant) =>
          catalogVariant(
            variant.id!,
            variant.name,
            optionVariantPrices(variant),
            // Opcja wycofana wycofuje wszystkie swoje warianty — wariant żywy
            // pod martwą opcją i tak nie jest do kupienia.
            !!option.archived || !!variant.archived,
          ),
        ),
    ]),
  ),
  transportVariants: (transport?.variants ?? [])
    .filter((variant) => !!variant.id)
    .map((variant) =>
      catalogVariant(
        variant.id!,
        variant.name,
        transportVariantPrices(variant),
      ),
    ),
  // Ubezpieczenie nie ma ceny w domenie: `Insurance` to `{ id, name,
  // description }` i wybór pakietu nie zakłada żadnej linii kosztowej.
  insurances: tourInsurances.insurances
    .filter((assignment) => !!assignment.insurance.id)
    .map((assignment) =>
      catalogVariant(
        assignment.insurance.id!,
        assignment.insurance.name,
        [],
        // Wycofać ubezpieczenie da się dwiema drogami — zdjęciem przypisania
        // z wyjazdu i schowaniem go w katalogu. Dla listy wyboru znaczą to samo.
        !isOfferedInsurance(assignment),
      ),
    ),
  insuranceRequired: tourInsurances.effectivelyRequired,
  customFieldOptions: customFieldOptionsOf(customFields),
});

/* Sekcje uczestnika. */

const packageSection = (
  participantId: string,
  lines: CostLineDto[],
): BillSection => {
  const baseLines = lines.filter(isTypeOf(CostLineType.BASE));
  const baseRef = ref(ChangeItemType.BASE_PRICE);

  const baseKey = costRowKey(participantId, CostLineType.BASE);

  const rows =
    baseLines.length > 0
      ? baseLines.map((costLine) =>
          line(costLine.id, 'Cena podstawowa', FieldType.MONEY_VALUE, baseRef, {
            value: costLine.basePriceName,
            amount: [costLine.amount],
            costLineIds: [costLine.id],
            costKey: baseKey,
          }),
        )
      : [
          line(
            `${participantId}:base-price`,
            'Cena podstawowa',
            FieldType.MONEY_VALUE,
            baseRef,
            { ...emptySelection(true), costKey: baseKey },
          ),
        ];

  return section(participantId, BillSectionId.PACKAGE, rows, {
    withAmounts: true,
  });
};

const participantSection = (participant: BookingParticipantDto): BillSection =>
  section(participant.id, BillSectionId.PARTICIPANT, [
    line(
      `${participant.id}:name`,
      'Imię i nazwisko',
      FieldType.TEXT,
      ref(ChangeItemType.PARTICIPANT_DATA, {
        fields: [
          ParticipantBillField.FIRST_NAME,
          ParticipantBillField.LAST_NAME,
        ],
      }),
      { value: `${participant.firstName} ${participant.lastName}` },
    ),
    line(
      `${participant.id}:birth-date`,
      'Data urodzenia',
      FieldType.DATE,
      ref(ChangeItemType.PARTICIPANT_DATA, {
        fields: [ParticipantBillField.BIRTH_DATE],
      }),
      participant.birthDate
        ? { value: formatDate(participant.birthDate) }
        : emptySelection(false),
    ),
  ]);

/** Wartość pola niestandardowego jako tekst wiersza; `null` = pole niewypełnione. */
const customValueText = (
  definition: CustomFieldDefinitionDto,
  value: CustomFieldValueDto | undefined,
): string | null => {
  if (!value) {
    return null;
  }
  switch (value.type) {
    case FieldType.CHECKBOX:
      return value.value ? 'Tak' : 'Nie';
    case FieldType.DROPDOWN: {
      const options = (definition as DropdownCustomFieldDefinitionDto).options;
      // Wartość `DROPDOWN` niesie UUID opcji, nie etykietę — etykietę zna
      // wyłącznie definicja pola.
      return (
        options?.find((option) => option.id === value.value)?.label ??
        value.value
      );
    }
    default:
      return value.value;
  }
};

/**
 * Identyfikator wybranej opcji listy — dla pola listy rozwijanej i tylko dla
 * niego. Wiersz pokazuje etykietę, ale formularz i pozycja paczki operują na
 * UUID-zie (`DropdownCustomFieldValueDto`), więc musi go skądś wziąć.
 */
const customValueOptionId = (
  value: CustomFieldValueDto | undefined,
): string | null => (value?.type === FieldType.DROPDOWN ? value.value : null);

const extraInfoSection = (
  participantId: string,
  definitions: CustomFieldDefinitionDto[],
  customValues: ParticipantCustomValuesDto,
): BillSection =>
  section(
    participantId,
    BillSectionId.EXTRA_INFO,
    definitions.map((definition) => {
      const value = customValues[definition.id];
      const text = customValueText(definition, value);
      return line(
        `${participantId}:custom:${definition.id}`,
        definition.name,
        definition.type,
        ref(ChangeItemType.CUSTOM_VALUES, {
          targetId: definition.id,
          currentVariantId: customValueOptionId(value),
        }),
        text === null ? emptySelection(false) : { value: text },
      );
    }),
  );

/**
 * Wiersz opcji wyjazdu — jeden na opcję, także wtedy, gdy uczestnik jej nie ma.
 *
 * Kwota bierze się z księgi (nazwy i ceny z chwili zakupu), a nie z katalogu;
 * przy wyborze wielokrotnym wiersz zbiera kwoty swoich linii, bo pozycja paczki
 * dotyczy całej opcji, nie pojedynczego wariantu.
 */
const optionLine = (
  participantId: string,
  option: TourOptionDto,
  selectedVariantIds: string[],
  optionLines: Extract<CostLineDto, { type: CostLineType.OPTION }>[],
): BillLine => {
  const multi = option.allowOptionMultiSelect;
  const lineRef = ref(ChangeItemType.OPTION, {
    targetId: option.id,
    currentVariantId: multi ? null : (selectedVariantIds[0] ?? null),
    currentVariantIds: multi ? selectedVariantIds : null,
  });
  const type = multi ? FieldType.MULTISELECT : FieldType.DROPDOWN;
  const id = `${participantId}:option:${option.id}`;
  const costKey = costRowKey(participantId, CostLineType.OPTION, option.id);

  if (selectedVariantIds.length === 0) {
    return line(id, option.name, type, lineRef, {
      ...emptySelection(true),
      costKey,
    });
  }

  const names = optionLines.map((costLine) => costLine.variantName);
  const amount = sumMoney(optionLines.map((costLine) => costLine.amount));

  return line(id, option.name, type, lineRef, {
    value: multi ? null : (names[0] ?? null),
    chips: multi ? names : null,
    amount,
    costLineIds: optionLines.map((costLine) => costLine.id),
    costKey,
  });
};

/**
 * Opcje, które rachunek uczestnika ma pokazać: żywe z oferty plus wycofane,
 * które uczestnik wciąż ma. Bez drugiego członu opcja zdjęta z oferty znikałaby
 * z rachunku razem z kwotą, którą uczestnik za nią zapłacił.
 */
const visibleOptions = (
  tourOptions: TourOptionDto[],
  selectedOptions: ParticipantSelectedOptionsDto,
  lines: CostLineDto[],
): TourOptionDto[] =>
  tourOptions.filter(
    (option) =>
      !option.archived ||
      (selectedOptions[option.id]?.length ?? 0) > 0 ||
      lines
        .filter(isTypeOf(CostLineType.OPTION))
        .some((costLine) => costLine.tourOptionId === option.id),
  );

const activitiesSection = (
  participantId: string,
  tourOptions: TourOptionDto[],
  selectedOptions: ParticipantSelectedOptionsDto,
  lines: CostLineDto[],
): BillSection => {
  const optionCostLines = lines.filter(isTypeOf(CostLineType.OPTION));

  return section(
    participantId,
    BillSectionId.ACTIVITIES,
    tourOptions.map((option) =>
      optionLine(
        participantId,
        option,
        (selectedOptions[option.id] ?? []).map(
          (selection) => selection.variantId,
        ),
        optionCostLines.filter(
          (costLine) => costLine.tourOptionId === option.id,
        ),
      ),
    ),
    { withAmounts: true },
  );
};

const transportInsuranceSection = (
  participantId: string,
  transport: TransportConfigurationWebDto | null,
  transportVariant: SelectedTransportVariantDto | null,
  tourInsurances: TourInsurancesDto,
  insurance: ParticipantInsuranceDto | null,
  lines: CostLineDto[],
): BillSection => {
  const rows: BillLine[] = [];

  if (transport?.transportOffering === TransportOffering.OFFERS_TRANSPORT) {
    const [costLine] = lines.filter(isTypeOf(CostLineType.TRANSPORT));
    rows.push(
      line(
        `${participantId}:transport`,
        'Transport',
        FieldType.DROPDOWN,
        ref(ChangeItemType.TRANSPORT, {
          currentVariantId: transportVariant?.variantId ?? null,
        }),
        {
          ...(transportVariant
            ? {
                value: costLine?.variantName ?? transportVariant.variantName,
                amount: costLine ? [costLine.amount] : [],
              }
            : emptySelection(true)),
          costLineIds: costLine ? [costLine.id] : [],
          costKey: costRowKey(participantId, CostLineType.TRANSPORT),
        },
      ),
    );
  }

  // Wyjazd bez żywej oferty wiersza nie ma — chyba że uczestnik ma
  // ubezpieczenie wycofane po jego wyborze; wtedy wiersz zostaje, żeby kwota
  // i wybór nie zniknęły bez śladu.
  if (tourInsurances.insurances.some(isOfferedInsurance) || insurance) {
    rows.push(
      line(
        `${participantId}:insurance`,
        'Ubezpieczenie',
        FieldType.DROPDOWN,
        ref(ChangeItemType.INSURANCE, {
          currentVariantId: insurance?.insurance.id ?? null,
        }),
        // Wiersz ubezpieczenia jest wyborem, nie pozycją rachunku: domena nie
        // zna linii kosztowej `INSURANCE`, więc kolumna kwot zostaje pusta.
        insurance
          ? {
              value: insurance.insurance.name,
              selectedAt: insurance.selectionDate,
            }
          : { value: NO_SELECTION_LABEL, muted: true },
      ),
    );
  }

  return section(participantId, BillSectionId.TRANSPORT_INSURANCE, rows, {
    withAmounts: true,
  });
};

/**
 * Zniżki przypadające na uczestnika. `ref: null` — paczka zmian nie ma typu
 * pozycji, którym dałoby się je ruszyć; sterują nimi ekrany rezerwacji.
 */
const discountsSection = (
  participantId: string,
  lines: CostLineDto[],
): BillSection =>
  section(
    participantId,
    BillSectionId.DISCOUNTS,
    lines.filter(isTypeOf(CostLineType.DISCOUNT)).map((costLine) =>
      line(costLine.id, costLine.discountName, FieldType.TEXT, null, {
        value: costLine.discountCode ?? 'zniżka z rezerwacji',
        muted: true,
        amount: [costLine.amount],
        locked: true,
        costLineIds: [costLine.id],
        costKey: costRowKey(
          participantId,
          CostLineType.DISCOUNT,
          costLine.discountId,
        ),
      }),
    ),
    { withAmounts: true },
  );

const manualLinesSection = (
  personId: string,
  sectionId: BillSectionId,
  lines: CostLineDto[],
): BillSection =>
  section(
    personId,
    sectionId,
    lines.filter(isTypeOf(CostLineType.MANUAL)).map((costLine) =>
      line(
        costLine.id,
        costLine.description,
        FieldType.MONEY_VALUE,
        ref(ChangeItemType.MANUAL_LINE, { targetId: costLine.id }),
        {
          amount: [costLine.amount],
          manual: true,
          costLineIds: [costLine.id],
          costKey: costRowKey(
            costLine.participantId,
            CostLineType.MANUAL,
            costLine.description,
          ),
        },
      ),
    ),
    { withAmounts: true, allowManualLines: true },
  );

/* Sekcje zapisującego. */

const CLIENT_EMAIL_NOTE =
  'Zmiana e-maila przekierowuje link akceptacyjny tej paczki na nowy adres.';

const clientSections = (client: BookingClientDto): BillSection[] => [
  section(
    'client',
    BillSectionId.CLIENT_CONTACT,
    [
      line(
        'client:name',
        'Imię i nazwisko',
        FieldType.TEXT,
        ref(ChangeItemType.CLIENT_DATA, {
          fields: [ClientBillField.FIRST_NAME, ClientBillField.LAST_NAME],
        }),
        { value: `${client.firstName} ${client.lastName}` },
      ),
      line(
        'client:email',
        'E-mail',
        FieldType.TEXT,
        ref(ChangeItemType.CLIENT_DATA, { fields: [ClientBillField.EMAIL] }),
        { value: client.email },
      ),
      line(
        'client:phone',
        'Numer telefonu',
        FieldType.PHONE_NUMBER,
        ref(ChangeItemType.CLIENT_DATA, { fields: [ClientBillField.PHONE] }),
        client.phoneNumber
          ? { value: formatPhoneNumber(client.phoneNumber) }
          : emptySelection(false),
      ),
      line(
        'client:birth-date',
        'Data urodzenia',
        FieldType.DATE,
        ref(ChangeItemType.CLIENT_DATA, {
          fields: [ClientBillField.BIRTH_DATE],
        }),
        client.birthDate
          ? { value: formatDate(client.birthDate) }
          : emptySelection(false),
      ),
      line(
        'client:address',
        'Adres',
        FieldType.TEXT,
        ref(ChangeItemType.CLIENT_DATA, {
          fields: [
            ClientBillField.STREET,
            ClientBillField.POSTAL_CODE,
            ClientBillField.CITY,
          ],
        }),
        client.street || client.city || client.postalCode
          ? {
              value:
                `${client.street ?? ''}, ${client.postalCode ?? ''} ${client.city ?? ''}`.trim(),
            }
          : emptySelection(false),
      ),
    ],
    { note: CLIENT_EMAIL_NOTE },
  ),
];

const toClientBill = (client: BookingClientDto): ClientBill => ({
  clientId: client.id,
  firstName: client.firstName,
  lastName: client.lastName,
  email: client.email,
  phoneNumber: client.phoneNumber,
  address: {
    street: client.street ?? '',
    postalCode: client.postalCode ?? '',
    city: client.city ?? '',
  },
  sections: clientSections(client),
});

/**
 * Wybory jednego uczestnika w kształcie, w jakim pyta o nie rachunek: kontrakt
 * zbiorczego odczytu bez klucza, po którym rachunek już go odnalazł.
 */
export type ParticipantSelections = Omit<
  ParticipantSelectionsDto,
  'participantId'
>;

export interface BookingBillSources {
  booking: BookingDetailsDto;
  bill: BookingBillDto;
  customFields: CustomFieldDefinitionDto[];
  tourOptions: TourOptionDto[];
  transport: TransportConfigurationWebDto | null;
  tourInsurances: TourInsurancesDto;
  /** Klucz to `participantId`; brak wpisu znaczy „uczestnik nie wybrał niczego”. */
  selections: Record<string, ParticipantSelections>;
  payments: BillPaymentDto[];
}

const EMPTY_SELECTIONS: ParticipantSelections = {
  customValues: {},
  selectedOptions: {},
  insurance: null,
  transportVariant: null,
};

const toParticipantBill = (
  participant: BookingParticipantDto,
  sources: BookingBillSources,
): ParticipantBill => {
  const lines = costLinesOf(sources.bill, participant.id);
  const selections = sources.selections[participant.id] ?? EMPTY_SELECTIONS;

  return {
    participantId: participant.id,
    firstName: participant.firstName,
    lastName: participant.lastName,
    birthDate: participant.birthDate,
    age: participant.ageAtTourStart,
    withdrawn: participant.status === ParticipantStatus.WITHDRAWN,
    sections: [
      packageSection(participant.id, lines),
      participantSection(participant),
      extraInfoSection(
        participant.id,
        sources.customFields,
        selections.customValues,
      ),
      activitiesSection(
        participant.id,
        visibleOptions(sources.tourOptions, selections.selectedOptions, lines),
        selections.selectedOptions,
        lines,
      ),
      transportInsuranceSection(
        participant.id,
        sources.transport,
        selections.transportVariant,
        sources.tourInsurances,
        selections.insurance,
        lines,
      ),
      discountsSection(participant.id, lines),
      manualLinesSection(participant.id, BillSectionId.MANUAL_LINES, lines),
      // Sekcja bez wierszy nie ma czego pokazać; „Pozycje ręczne” zostają
      // zawsze, bo to one przyjmują wolne linie.
    ].filter(
      (billSection) =>
        billSection.lines.length > 0 || billSection.allowManualLines,
    ),
  };
};

export const bookingBillDtoMapper = {
  /**
   * Wybory rezerwacji pod klucz uczestnika. Odpowiedź idzie za uczestnikami
   * rezerwacji, ale wiązać ją wolno wyłącznie po `participantId` — kolejność
   * jest szczegółem zapytania, a nie częścią kontraktu.
   */
  selectionsByParticipant(
    selections: ParticipantSelectionsDto[],
  ): Record<string, ParticipantSelections> {
    return Object.fromEntries(
      selections.map(({ participantId, ...selection }) => [
        participantId,
        selection,
      ]),
    );
  },

  /** Rachunek rezerwacji złożony ze wszystkich źródeł. */
  toBookingBill(sources: BookingBillSources): BookingBill {
    const { booking, bill } = sources;

    return {
      bookingId: booking.bookingId,
      bookingCode: booking.bookingCode,
      tourId: booking.tourId,
      tourName: booking.tourName,
      tourStartDate: booking.tourStartDate,
      tourEndDate: booking.tourEndDate,
      status: booking.status,
      client: toClientBill(booking.client),
      participants: booking.participants.map((participant) =>
        toParticipantBill(participant, sources),
      ),
      bookingWideSections: [
        manualLinesSection(
          'booking',
          BillSectionId.BOOKING_ADJUSTMENTS,
          costLinesOf(bill, null),
        ),
      ],
      installments: booking.installments,
      payments: sources.payments,
      balances: booking.balances,
      totals: bill.totals,
      totalsByParticipant: bill.totalsByParticipant,
      catalog: buildCatalog(
        sources.customFields,
        sources.tourOptions,
        sources.transport,
        sources.tourInsurances,
      ),
    };
  },
};
